// Schedules every model call (DEPTH.md §3 "Scheduling"; ios AIService.swift): now > visible > prefetch > background,
// at most 6 in flight of which prefetch/background take at most 3; background keeps chapter order, the rest newest
// first. A note waits for its chapter's structure. Before generating, the item's synced rows are fetched (§8).
import { textModel } from "../models";
import { settings } from "../settings";
import { Hub, isAbort, sleep } from "../util";
import { documentsByKey, type DepthDocument } from "./document";
import { levelsStore as levels } from "./levelsStore";
import * as P from "./prompts";
import { AIError, TextClient, unavailable } from "./textClient";
import { isFiction } from "./types";

export enum Priority { Background = 0, Prefetch = 1, Visible = 2, Now = 3 }
export type Job = { kind: "structure"; key: string; chapter: number } | { kind: "note"; key: string; chapter: number; beat: number };
export type JobState = { state: "queued" } | { state: "running" } | { state: "failed"; message: string; noKey: boolean };

const jobId = (j: Job) => (j.kind === "structure" ? `s|${j.key}|${j.chapter}` : `n|${j.key}|${j.chapter}|${j.beat}`);

const MAX_CONCURRENT = 6, MAX_LOW = 3;

class AIService {
  private states = new Map<string, JobState>();
  readonly partial = new Map<string, string>();
  private queue = new Map<string, { job: Job; priority: Priority; seq: number }>();
  private waiting = new Map<string, { job: Job; priority: Priority }>();
  private running = new Map<string, { priority: Priority; ctrl: AbortController }>();
  private seq = 0;
  readonly events = new Hub<{ changed: { key: string; chapter: number } }>();
  /** DEPTH.md §8: resolves once the item's rows were fetched (≤3 s). Set by LevelsSync. */
  ready: (key: string) => Promise<void> = async () => {};

  state(j: Job): JobState | undefined { return this.states.get(jobId(j)); }
  partialNote(key: string, chapter: number, beat: number) { return this.partial.get(jobId({ kind: "note", key, chapter, beat })); }

  requestStructure(key: string, chapter: number, priority: Priority) {
    if (levels.structure(key, chapter)) return;
    this.enqueue({ kind: "structure", key, chapter }, priority);
  }

  requestNote(key: string, chapter: number, beat: number, priority: Priority) {
    if (levels.note(key, chapter, beat) !== null) return;
    const job: Job = { kind: "note", key, chapter, beat };
    if (!levels.structure(key, chapter)) {
      const id = jobId(job), w = this.waiting.get(id);
      this.waiting.set(id, { job, priority: Math.max(w?.priority ?? priority, priority) });
      this.requestStructure(key, chapter, priority);
      return;
    }
    this.enqueue(job, priority);
  }

  requestWholeDocument(key: string, around: number) {
    const doc = documentsByKey.get(key);
    if (!doc) return;
    doc.chapters.forEach((c, i) => {
      if (!c.paragraphs.length) return;
      this.requestStructure(key, i, i === around ? Priority.Now : Math.abs(i - around) === 1 ? Priority.Prefetch : Priority.Background);
    });
  }

  retry(j: Job) {
    this.states.delete(jobId(j));
    if (j.kind === "structure") this.requestStructure(j.key, j.chapter, Priority.Now);
    else this.requestNote(j.key, j.chapter, j.beat, Priority.Now);
  }

  /** After signing in: retry what failed for lack of access. */
  accessChanged() {
    for (const [id, s] of [...this.states]) if (s.state === "failed") {
      const parts = id.split("|");
      this.retry(parts[0] === "s" ? { kind: "structure", key: parts[1], chapter: +parts[2] } : { kind: "note", key: parts[1], chapter: +parts[2], beat: +parts[3] });
    }
  }

  cancel(key: string, keepVisible = false) {
    for (const [id, q] of [...this.queue]) if (q.job.key === key && (!keepVisible || q.priority < Priority.Visible)) { this.queue.delete(id); this.states.delete(id); }
    for (const [id, w] of [...this.waiting]) if (w.job.key === key) this.waiting.delete(id);
    if (!keepVisible) for (const [id, r] of this.running) if (id.split("|")[1] === key) r.ctrl.abort();
  }

  private enqueue(job: Job, priority: Priority) {
    const id = jobId(job);
    const st = this.states.get(id);
    if (st?.state === "running") return;
    if (st?.state === "failed" && priority < Priority.Now && (!st.noKey || unavailable(textModel()))) return;
    this.seq += 1;
    const prev = this.queue.get(id);
    const p = Math.max(prev?.priority ?? priority, priority);
    this.queue.set(id, { job, priority: p, seq: p >= Priority.Visible ? this.seq : (prev?.seq ?? this.seq) });
    this.states.set(id, { state: "queued" });
    this.pump();
  }

  private pump() {
    while (this.running.size < MAX_CONCURRENT) {
      const lowBusy = [...this.running.values()].filter((r) => r.priority < Priority.Visible).length;
      let best: [string, { job: Job; priority: Priority; seq: number }] | null = null;
      for (const e of this.queue) {
        if (e[1].priority < Priority.Visible && lowBusy >= MAX_LOW) continue;
        if (!best) { best = e; continue; }
        const a = best[1], b = e[1];
        const better = b.priority !== a.priority ? b.priority > a.priority
          : b.priority >= Priority.Visible ? b.seq > a.seq : b.seq < a.seq;
        if (better) best = e;
      }
      if (!best) break;
      const [id, { job, priority }] = best;
      this.queue.delete(id);
      const ctrl = new AbortController();
      this.running.set(id, { priority, ctrl });
      this.states.set(id, { state: "running" });
      this.run(job, ctrl.signal).then(() => this.finish(job, null), (e) => this.finish(job, e));
    }
  }

  private finish(job: Job, error: unknown) {
    const id = jobId(job);
    this.running.delete(id);
    this.partial.delete(id);
    if (error && !isAbort(error)) {
      const noKey = error instanceof AIError && error.isNoKey;
      this.states.set(id, { state: "failed", message: error instanceof Error ? error.message : String(error), noKey });
    } else this.states.delete(id);
    if (job.kind === "structure") {
      for (const [nid, w] of [...this.waiting]) {
        if (w.job.key !== job.key || w.job.chapter !== job.chapter) continue;
        this.waiting.delete(nid);
        const st = this.states.get(id);
        if (!error) this.enqueue(w.job, w.priority);
        else if (st?.state === "failed") this.states.set(nid, st);
      }
    }
    this.events.emit("changed", { key: job.key, chapter: job.chapter });
    this.pump();
  }

  private async run(job: Job, signal: AbortSignal) {
    await this.ready(job.key);
    if (job.kind === "structure" ? levels.structure(job.key, job.chapter) : levels.note(job.key, job.chapter, job.beat) !== null) return;
    let delay = 1500;
    for (let attempt = 0; ; attempt++) {
      try {
        if (job.kind === "structure") return await this.runStructure(job.key, job.chapter, signal);
        return await this.runNote(job, signal);
      } catch (e) {
        const retry = attempt < 2 && ((e instanceof AIError && e.isRetryable) || (e instanceof TypeError && !signal.aborted));
        if (!retry) throw e;
        await sleep(delay, signal);
        delay *= 2.5;
      }
    }
  }

  private client() { return new TextClient(textModel()); }

  private async runStructure(key: string, chapter: number, signal: AbortSignal) {
    const doc = documentsByKey.get(key);
    const ch = doc?.chapters[chapter];
    if (!doc || !ch) return;
    const prompt = P.structure(doc.title, doc.author, { title: ch.title, paragraphs: ch.paragraphs });
    let last: unknown = null;
    for (let i = 0; i < 2; i++) {
      const text = await this.client().complete(prompt.system, prompt.user, { json: true, maxTokens: 8000, effort: "medium", signal });
      try {
        levels.setStructure(P.parseStructure(text, ch.paragraphs.length), key, chapter);
        return;
      } catch (e) { last = e; }
    }
    throw last ?? new P.BadResponse("Unknown");
  }

  private async runNote(job: Extract<Job, { kind: "note" }>, signal: AbortSignal) {
    const { key, chapter, beat } = job;
    const doc = documentsByKey.get(key);
    const s = levels.structure(key, chapter);
    const ch = doc?.chapters[chapter];
    if (!doc || !ch || !s || !s.beats[beat]) return;
    const b = s.beats[beat];
    const before = ch.paragraphs.slice(0, b.from).map((p) => p.text).join("\n\n");
    const passage = ch.paragraphs.slice(b.from, b.to + 1).map((p) => p.text).join("\n\n");
    const textSoFar = bookSoFar(doc, chapter);
    const explained = levels.explainedTerms(key, chapter, beat);
    const fiction = isFiction(s) && doc.isBook;
    const checkSpoilers = fiction && settings.spoilerCheck;
    const prompt = P.note({ bookTitle: doc.title, author: doc.author, textSoFar, chapterTitle: ch.title, before, passage, fiction, explained });
    const id = jobId(job);
    const client = this.client();
    const draft = await client.complete(prompt.system, prompt.user, { maxTokens: 400, temperature: 0.4, signal,
      cachePrefix: cachePrefix(prompt.user, textSoFar),
      onDelta: checkSpoilers ? undefined : (t) => { this.partial.set(id, P.sanitizeNote(t)); this.events.emit("changed", { key, chapter }); } });
    let final = draft;
    if (checkSpoilers) {
      const c = P.check(textSoFar + before, passage, draft, explained);
      try {
        final = P.parseCheck(await client.complete(c.system, c.user, { json: true, maxTokens: 600, temperature: 0, signal, cachePrefix: cachePrefix(c.user, textSoFar) }), draft);
      } catch (e) { if (isAbort(e)) throw e; }
    }
    levels.setNote(P.sanitizeNote(final), key, chapter, beat);
  }
}

export function cachePrefix(user: string, textSoFar: string): string | null {
  if (!textSoFar) return null;
  const i = user.indexOf(textSoFar);
  return i < 0 ? null : user.slice(0, i + textSoFar.length);
}

export function bookSoFar(doc: DepthDocument, chapter: number, limit = 600_000): string {
  if (chapter <= 0) return "";
  const parts: string[] = [];
  let total = 0;
  for (let c = chapter - 1; c >= 0; c--) {
    const ch = doc.chapters[c];
    const text = `## ${ch.title}\n\n` + ch.paragraphs.map((p) => p.text).join("\n\n") + "\n\n";
    if (total + text.length > limit) break;
    parts.push(text); total += text.length;
  }
  return parts.reverse().join("");
}

export const ai = new AIService();
